import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Inbox, Send, FileEdit, Trash2, AlertOctagon, Archive, Folder, RefreshCw, LogOut, CalendarClock,
  ChevronLeft, ChevronRight, ChevronDown, Loader2, Mail, MailOpen, PenLine, Star, FolderInput,
  Search, X, MailPlus, ArrowUpDown, Calendar, Users, ListTodo, Settings, MailCheck, Clock3, Tag,
  Reply, ReplyAll, Forward, Flag, Bell, Menu, Filter, ShieldAlert, PenSquare, Printer, Pin,
} from "lucide-react";
import { LOGO } from "../../data/content";
import { avatarColor, SenderAvatar } from "./MailAvatar";
export { avatarColor, SenderAvatar };
import { ComposeModal } from "./ComposeModal";
import { SnoozePopover, CategoryPopover, UndoSendBar, CategoryDots } from "./MailExtras";
import { MessageView } from "./MessageView";
import { SignatureModal } from "./SignatureModal";
import { ProfileModal } from "./ProfileModal";
import { AddAccountModal } from "./AddAccountModal";
import { SettingsModal } from "./SettingsModal";
import { CalendarView } from "./CalendarView";
import { ContactsView } from "./ContactsView";
import { TasksView } from "./TasksView";
import { ConversationView } from "./ConversationView";

const OUTLOOK_FONT = { fontFamily: '"Segoe UI", "Segoe UI Web (West European)", -apple-system, system-ui, sans-serif' };

const APP_NAV = [
  { id: "mail", label: "Mail", Icon: Mail },
  { id: "calendar", label: "Calendar", Icon: Calendar },
  { id: "people", label: "People", Icon: Users },
  { id: "todo", label: "To Do", Icon: ListTodo },
];

const FOLDER_ICONS = { Inbox, Sent: Send, Drafts: FileEdit, Trash: Trash2, Spam: AlertOctagon, Archive };

const fmtDay = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const today = new Date();
  const sameDay = dt.toDateString() === today.toDateString();
  return sameDay
    ? dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
    : dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const SORT_OPTIONS = [
  ["date_desc", "Newest first"],
  ["date_asc", "Oldest first"],
  ["from_asc", "Sender A–Z"],
  ["from_desc", "Sender Z–A"],
  ["subject_asc", "Subject A–Z"],
  ["subject_desc", "Subject Z–A"],
];

const quoteOriginal = (msg, label) =>
  `\n\n--- ${label} message ---\nFrom: ${msg.from?.name || ""} <${msg.from?.email || ""}>\nDate: ${new Date(msg.date).toLocaleString("en-IN")}\nSubject: ${msg.subject}\n\n${msg.text || ""}`;

// Outlook-style conversation grouping (client-side, by normalized subject)
const normSubject = (s = "") => s.replace(/^(\s*(re|fwd?|fw|aw|wg)\s*:\s*)+/i, "").trim().toLowerCase() || "(no subject)";
const buildThreads = (rows) => {
  const map = new Map();
  const order = [];
  for (const m of rows) {
    const key = normSubject(m.subject);
    if (!map.has(key)) { map.set(key, []); order.push(key); }
    map.get(key).push(m);
  }
  return order.map((key) => {
    const messages = map.get(key);
    return {
      key,
      messages,
      latest: messages[0],
      count: messages.length,
      unseen: messages.some((x) => !x.seen),
      flagged: messages.some((x) => x.flagged),
    };
  });
};

export const MailApp = ({ api, user, onLogout }) => {
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("INBOX");
  const [scheduled, setScheduled] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [allFolder, setAllFolder] = useState(false);
  const [listFilter, setListFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [sortOpen, setSortOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [listing, setListing] = useState({ messages: [], total: 0, page: 1, pages: 1 });
  const [listLoading, setListLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [searchInfo, setSearchInfo] = useState(null); // {q, total}
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [conversation, setConversation] = useState(null); // {messages, count, key} when a thread is opened
  const [msgLoading, setMsgLoading] = useState(false);
  const [compose, setCompose] = useState(null);
  const [sigModal, setSigModal] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileModal, setProfileModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [activeEmail, setActiveEmail] = useState(user.email);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [appView, setAppView] = useState("mail");
  const [contacts, setContacts] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [mobileReading, setMobileReading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [pins, setPins] = useState(new Set());
  const [popover, setPopover] = useState(null);
  const [undoSend, setUndoSend] = useState(null);
  const [inboxTab, setInboxTab] = useState("focused"); // Outlook Focused / Other
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expanded, setExpanded] = useState(new Set()); // expanded conversation keys
  const [collapsedAccounts, setCollapsedAccounts] = useState(new Set()); // accounts the user collapsed (all collapsed on login)
  const didInitCollapse = useRef(false);
  const schedSnapshotRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState(null); // {m, x, y}
  const [ctxMoveOpen, setCtxMoveOpen] = useState(false);

  // Only HR, Accounts and the CEO can add/link another mailbox.
  const canAddAccount =
    ["ceo@nexsecure.in", "hr@nexsecure.in", "accounts@nexsecure.in"].includes((user.email || "").toLowerCase()) ||
    profile?.role === "admin";

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => { setCtxMenu(null); setCtxMoveOpen(false); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => { window.removeEventListener("click", close); window.removeEventListener("scroll", close, true); };
  }, [ctxMenu]);

  // collapse every mailbox on first load; user expands by clicking the chevron
  useEffect(() => {
    if (!didInitCollapse.current && accounts.length) {
      didInitCollapse.current = true;
      setCollapsedAccounts(new Set(accounts.map((a) => a.email)));
    }
  }, [accounts]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/extras/categories");
      setCategories(data.categories);
      setAssignments(data.assignments);
    } catch {}
  }, [api]);
  useEffect(() => { loadCategories(); }, [loadCategories, activeEmail]);

  const loadPins = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/extras/pins");
      setPins(new Set(data.message_ids || []));
    } catch {}
  }, [api]);
  useEffect(() => { loadPins(); }, [loadPins, activeEmail]);

  const togglePin = async (message_id) => {
    if (!message_id) { toast.error("This message can't be pinned"); return; }
    const willPin = !pins.has(message_id);
    setPins((s) => { const n = new Set(s); willPin ? n.add(message_id) : n.delete(message_id); return n; });
    try {
      await api.post("/mail/extras/pins", { message_id, pinned: willPin });
      toast.success(willPin ? "Pinned to top" : "Unpinned");
    } catch {
      setPins((s) => { const n = new Set(s); willPin ? n.delete(message_id) : n.add(message_id); return n; });
      toast.error("Could not update pin");
    }
  };

  const printMessage = () => {
    if (!msg) { toast.error("Open a message to print"); return; }
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const fromLine = `${esc(msg.from?.name || "")} &lt;${esc(msg.from?.email || "")}&gt;`;
    const toLine = esc((msg.to || []).map((a) => a.email).join(", "));
    const when = msg.date ? esc(new Date(msg.date).toLocaleString("en-IN")) : "";
    const subject = esc(msg.subject || "(no subject)");
    const body = msg.html || (msg.text ? `<pre style="white-space:pre-wrap;font:14px/1.6 Segoe UI,sans-serif">${esc(msg.text)}</pre>` : "");
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(`<!doctype html><html><head><title>${subject}</title>
      <style>body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a18;margin:32px}
      .hdr{border-bottom:2px solid #0a1526;padding-bottom:14px;margin-bottom:18px}
      .hdr h1{font-size:20px;margin:0 0 10px} .meta{font-size:13px;color:#555;line-height:1.7}
      .meta b{color:#1a1a18}</style></head><body>
      <div class="hdr"><h1>${subject}</h1>
      <div class="meta"><div><b>From:</b> ${fromLine}</div><div><b>To:</b> ${toLine}</div><div><b>Date:</b> ${when}</div></div></div>
      <div>${body}</div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 350);
  };

  const snoozeMessage = async (m, untilIso) => {
    setPopover(null);
    try {
      await api.post("/mail/extras/snooze", { folder, uid: String(m.uid), until: untilIso });
      setListing((l) => ({ ...l, messages: l.messages.filter((x) => x.uid !== m.uid) }));
      if (msg?.uid === m.uid) setMsg(null);
      toast.success(`Snoozed — back in your inbox ${new Date(untilIso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not snooze");
    }
  };

  const undoNow = async () => {
    const u = undoSend;
    setUndoSend(null);
    try {
      await api.post(`/mail/scheduled/${u.sid}/cancel`);
      setCompose({ ...u.snapshot });
      toast.success("Sending cancelled — your message is back in compose (auto-saves to Drafts)");
    } catch {
      toast.error("Too late — the email was already sent");
    }
  };

  const draftsFolder = folders.find((f) => f.display === "Drafts")?.name;

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/folders");
      setFolders(data.folders);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  }, [api, onLogout]);

  const loadMessages = useCallback(
    async (f = folder, p = 1, quiet = false, filt = listFilter, sort = sortBy) => {
      if (!quiet) setListLoading(true);
      try {
        const { data } = await api.get("/mail/messages", { params: { folder: f, page: p, filter: filt === "all" ? "" : filt, sort } });
        setListing(data);
      } catch (err) {
        if (err?.response?.status === 401) onLogout();
        else if (!quiet) toast.error("Could not load messages");
      } finally {
        setListLoading(false);
      }
    },
    [api, folder, onLogout, listFilter, sortBy]
  );

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/profile");
      setProfile(data);
    } catch {}
  }, [api]);

  const loadScheduled = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/scheduled");
      const items = data.items || [];
      // Detect pending -> sent/failed transitions and toast instant confirmation.
      const prev = schedSnapshotRef.current;
      if (prev) {
        for (const it of items) {
          const was = prev[it.id];
          if (was === "pending" && it.status !== "pending") {
            const rcpt = (it.to || "").split(",")[0].trim() || "recipient";
            if (it.status === "sent") toast.success(`Scheduled mail to ${rcpt} sent`, { description: it.subject || "(no subject)" });
            else if (it.status === "failed") toast.error(`Scheduled mail to ${rcpt} failed to send`, { description: it.subject || "(no subject)" });
          }
        }
      }
      schedSnapshotRef.current = Object.fromEntries(items.map((it) => [it.id, it.status]));
      setScheduled(items);
    } catch {}
  }, [api]);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/accounts");
      setAccounts(data.accounts || []);
      setActiveEmail(data.active || user.email);
    } catch {}
  }, [api, user.email]);

  const loadSignatureFlag = useCallback(() => {
    api.get("/mail/signature").then(({ data }) => setHasSignature(Boolean(data.signature))).catch(() => {});
  }, [api]);

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/contacts");
      setContacts(data.contacts || []);
    } catch {}
  }, [api]);

  const cancelScheduled = async (id) => {
    try {
      await api.post(`/mail/scheduled/${id}/cancel`);
      toast.success("Scheduled send cancelled");
      loadScheduled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    }
  };

  useEffect(() => {
    loadFolders();
    loadMessages("INBOX", 1);
    loadProfile();
    loadScheduled();
    loadAccounts();
    loadSignatureFlag();
    loadContacts();
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      if (folder === "__scheduled") loadScheduled();
      else if (!searchInfo) loadMessages(folder, listing.page, true);
      loadFolders();
      loadAccounts();
    }, 90000);
    return () => clearInterval(t);
  }, [folder, listing.page, searchInfo, loadMessages, loadFolders, loadScheduled, loadAccounts]);

  // Background poll for scheduled sends so staff get an instant on-screen toast when one goes out.
  useEffect(() => {
    const t = setInterval(() => loadScheduled(), 30000);
    return () => clearInterval(t);
  }, [loadScheduled]);

  const switchFolder = (name) => {
    setFolder(name);
    setMsg(null);
    setConversation(null);
    setMobileReading(false);
    setSelected(new Set());
    setAllFolder(false);
    setMoveOpen(false);
    setSearchInfo(null);
    setSearchQ("");
    setInboxTab("focused");
    if (name === "__scheduled") loadScheduled();
    else loadMessages(name, 1);
  };

  const switchAccount = async (email) => {
    if (email === activeEmail) return;
    try {
      await api.post("/mail/accounts/switch", { email });
      toast.success(`Switched to ${email}`);
      setActiveEmail(email);
      setMsg(null);
      setConversation(null);
      setMobileReading(false);
      setSelected(new Set());
      setAllFolder(false);
      setSearchInfo(null);
      setSearchQ("");
      setFolder("INBOX");
      loadFolders();
      loadMessages("INBOX", 1);
      loadProfile();
      loadScheduled();
      loadSignatureFlag();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch mailbox");
    }
  };

  const removeAccount = async (email) => {
    if (!window.confirm(`Remove ${email} from this sign-in? The mailbox itself is not deleted.`)) return;
    try {
      await api.delete(`/mail/accounts/${email}`);
      toast.success(`Removed ${email}`);
      loadAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove that mailbox");
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    const q = searchQ.trim();
    if (q.length < 2) {
      toast.error("Type at least 2 characters to search");
      return;
    }
    setSearchBusy(true);
    try {
      const { data } = await api.get("/mail/search", { params: { q, folder } });
      setSearchResults(data.messages);
      setSearchInfo({ q: data.q, total: data.total });
      setSelected(new Set());
      setAllFolder(false);
      setMsg(null);
      setConversation(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setSearchBusy(false);
    }
  };

  const clearSearch = () => {
    setSearchInfo(null);
    setSearchResults([]);
    setSearchQ("");
    loadMessages(folder, 1);
  };

  const applySort = (sort) => {
    setSortBy(sort);
    setSortOpen(false);
    setSelected(new Set());
    setAllFolder(false);
    loadMessages(folder, 1, false, listFilter, sort);
  };

  const toggleSelect = (uid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(uid)) n.delete(uid); else n.add(uid);
    return n;
  });

  const applyFilter = (filt) => {
    setListFilter(filt);
    setSelected(new Set());
    setAllFolder(false);
    loadMessages(folder, 1, false, filt);
  };

  const BULK_LABELS = { seen: "Marked read", unseen: "Marked unread", flag: "Flagged", unflag: "Unflagged", archive: "Archived", move: "Moved", delete: "Moved to Trash", spam: "Marked as spam" };

  const bulkAction = async (action, target) => {
    if (!allFolder && !selected.size) return;
    setBulkBusy(true);
    try {
      const scope = allFolder ? { all: true } : { uids: Array.from(selected) };
      const params = { folder };
      if (action === "seen" || action === "unseen") {
        const { data } = await api.post("/mail/messages/seen-bulk", { ...scope, seen: action === "seen" }, { params });
        toast.success(`${BULK_LABELS[action]}${allFolder ? ` — whole folder (${data.updated})` : ""}`);
      } else if (action === "flag" || action === "unflag") {
        await api.post("/mail/messages/flag-bulk", { ...scope, flagged: action === "flag" }, { params });
        toast.success(BULK_LABELS[action]);
      } else if (action === "delete") {
        await api.post("/mail/messages/delete-bulk", { ...scope, flagged: true }, { params });
        toast.success(BULK_LABELS[action]);
      } else {
        const dest = action === "archive" ? "Archive" : target;
        await api.post("/mail/messages/move-bulk", { ...scope, target: dest }, { params });
        toast.success(BULK_LABELS[action]);
      }
      setSelected(new Set());
      setAllFolder(false);
      setMoveOpen(false);
      loadMessages(folder, listing.page);
      loadFolders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const quickAction = async (e, m, action) => {
    e.stopPropagation();
    try {
      if (action === "toggleSeen") {
        await api.post(`/mail/message/${m.uid}/seen`, { seen: !m.seen }, { params: { folder } });
        setListing((l) => ({ ...l, messages: l.messages.map((x) => (x.uid === m.uid ? { ...x, seen: !m.seen } : x)) }));
        loadFolders();
      } else if (action === "toggleFlag") {
        await api.post("/mail/messages/flag-bulk", { uids: [m.uid], flagged: !m.flagged }, { params: { folder } });
        setListing((l) => ({ ...l, messages: l.messages.map((x) => (x.uid === m.uid ? { ...x, flagged: !m.flagged } : x)) }));
      } else if (action === "archive") {
        await api.post("/mail/messages/move-bulk", { uids: [m.uid], target: "Archive" }, { params: { folder } });
        setListing((l) => ({ ...l, messages: l.messages.filter((x) => x.uid !== m.uid), total: Math.max(0, l.total - 1) }));
        if (msg?.uid === m.uid) setMsg(null);
        loadFolders();
        toast.success("Archived");
      } else if (action === "delete") {
        await api.delete(`/mail/message/${m.uid}`, { params: { folder } });
        setListing((l) => ({ ...l, messages: l.messages.filter((x) => x.uid !== m.uid), total: Math.max(0, l.total - 1) }));
        if (msg?.uid === m.uid) setMsg(null);
        loadFolders();
        toast.success("Moved to Trash");
      }
    } catch {
      toast.error("Action failed");
    }
  };

  const spamFolder = folders.find((f) => /spam|junk/i.test(f.name))?.name;
  const movableFolders = folders.filter((f) => f.name !== folder);
  const isDrafts = folders.find((f) => f.name === folder)?.display === "Drafts";

  const toggleThread = (key) => setExpanded((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const selectThread = (messages) => setSelected((s) => {
    const n = new Set(s);
    const allIn = messages.every((m) => n.has(m.uid));
    messages.forEach((m) => (allIn ? n.delete(m.uid) : n.add(m.uid)));
    return n;
  });

  const moveOne = async (m, target) => {
    try {
      await api.post("/mail/messages/move-bulk", { uids: [m.uid], target }, { params: { folder } });
      setListing((l) => ({ ...l, messages: l.messages.filter((x) => x.uid !== m.uid), total: Math.max(0, l.total - 1) }));
      if (msg?.uid === m.uid) setMsg(null);
      loadFolders();
      toast.success(`Moved to ${target}`);
    } catch {
      toast.error("Action failed");
    }
  };

  const ctxAction = (kind, target) => {
    const m = ctxMenu?.m;
    setCtxMenu(null); setCtxMoveOpen(false);
    if (!m) return;
    if (kind === "move") moveOne(m, target);
    else quickAction({ stopPropagation() {} }, m, kind);
  };

  const openMessage = async (m) => {
    setConversation(null);
    setMsgLoading(true);
    setMobileReading(true);
    try {
      const { data } = await api.get(`/mail/message/${m.uid}`, { params: { folder } });
      if (isDrafts) {
        setCompose({
          to: (data.to || []).map((a) => a.email).join(", "),
          cc: (data.cc || []).map((a) => a.email).join(", "),
          bcc: (data.bcc || []).map((a) => a.email).join(", "),
          subject: data.subject === "(no subject)" ? "" : data.subject,
          body: data.text || "",
          body_html: data.html || "",
          draft_uid: m.uid,
          draftsFolder: folder,
        });
        setMobileReading(false);
        return;
      }
      setMsg(data);
      if (!m.seen) {
        api.post(`/mail/message/${m.uid}/seen`, { seen: true }, { params: { folder } }).catch(() => {});
        setListing((l) => ({ ...l, messages: l.messages.map((x) => (x.uid === m.uid ? { ...x, seen: true } : x)) }));
        setFolders((fs) => fs.map((f) => (f.name === folder ? { ...f, unseen: Math.max(0, f.unseen - 1) } : f)));
      }
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Could not open the message");
      setMobileReading(false);
    } finally {
      setMsgLoading(false);
    }
  };

  const openConversation = (thread) => {
    setMsg(null);
    setConversation(thread);
    setMobileReading(true);
    // reflect read state in the list for the whole thread as cards are opened
    if (thread.unseen) {
      setListing((l) => ({ ...l, messages: l.messages.map((x) => (thread.messages.some((tm) => tm.uid === x.uid) && !x.seen ? { ...x, seen: true } : x)) }));
    }
  };

  const markRead = (uid) => {
    setListing((l) => ({ ...l, messages: l.messages.map((x) => (x.uid === uid ? { ...x, seen: true } : x)) }));
    setFolders((fs) => fs.map((f) => (f.name === folder ? { ...f, unseen: Math.max(0, f.unseen - 1) } : f)));
  };

  const forwardMessage = (m) => {
    setCompose({
      to: "",
      subject: (m.subject || "").startsWith("Fwd:") ? m.subject : `Fwd: ${m.subject}`,
      body: quoteOriginal(m, "Forwarded"),
    });
  };

  const afterReplySent = () => {
    loadFolders();
    if (folder.toLowerCase().startsWith("sent")) loadMessages(folder, 1);
  };

  const removeMessage = async () => {
    if (!msg || actionBusy) return;
    setActionBusy(true);
    try {
      await api.delete(`/mail/message/${msg.uid}`, { params: { folder } });
      toast.success("Message deleted");
      setMsg(null);
      setMobileReading(false);
      loadMessages(folder, listing.page);
      loadFolders();
    } catch {
      toast.error("Could not delete");
    } finally {
      setActionBusy(false);
    }
  };

  const toggleSeen = async (seen) => {
    if (!msg) return;
    try {
      await api.post(`/mail/message/${msg.uid}/seen`, { seen }, { params: { folder } });
      setMsg({ ...msg, seen });
      setListing((l) => ({ ...l, messages: l.messages.map((x) => (x.uid === msg.uid ? { ...x, seen } : x)) }));
      loadFolders();
    } catch {
      toast.error("Could not update");
    }
  };

  const markAllRead = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/mail/messages/seen-bulk", { all: true, seen: true }, { params: { folder } });
      toast.success(`Marked ${data.updated} message${data.updated === 1 ? "" : "s"} as read — whole folder`);
      loadMessages(folder, listing.page);
      loadFolders();
      loadAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the folder");
    } finally {
      setBulkBusy(false);
    }
  };

  const reply = () => {
    if (!msg) return;
    setCompose({
      to: msg.from?.email || "",
      subject: msg.subject.startsWith("Re:") ? msg.subject : `Re: ${msg.subject}`,
      body: quoteOriginal(msg, "Original"),
    });
  };

  const replyAll = () => {
    if (!msg) return;
    const self = (activeEmail || "").toLowerCase();
    const seenEmails = new Set([self]);
    const tos = [msg.from, ...(msg.to || [])].filter((a) => {
      const e = (a?.email || "").toLowerCase();
      if (!e || seenEmails.has(e)) return false;
      seenEmails.add(e);
      return true;
    });
    const ccs = (msg.cc || []).filter((a) => {
      const e = (a?.email || "").toLowerCase();
      if (!e || seenEmails.has(e)) return false;
      seenEmails.add(e);
      return true;
    });
    setCompose({
      to: tos.map((a) => a.email).join(", "),
      cc: ccs.map((a) => a.email).join(", "),
      subject: msg.subject.startsWith("Re:") ? msg.subject : `Re: ${msg.subject}`,
      body: quoteOriginal(msg, "Original"),
    });
  };

  const forward = () => {
    if (!msg) return;
    setCompose({
      to: "",
      subject: msg.subject.startsWith("Fwd:") ? msg.subject : `Fwd: ${msg.subject}`,
      body: quoteOriginal(msg, "Forwarded"),
    });
  };

  // ---- Ribbon actions: operate on selection, else the open message ----
  const hasSel = () => allFolder || selected.size > 0;
  const ribbonDelete = () => { if (hasSel()) bulkAction("delete"); else if (msg) removeMessage(); else toast.error("Select a message first"); };
  const ribbonArchive = () => { if (hasSel()) bulkAction("archive"); else if (msg) quickAction({ stopPropagation() {} }, msg, "archive"); else toast.error("Select a message first"); };
  const ribbonReport = () => { if (!spamFolder) return toast.error("No junk folder"); if (hasSel()) bulkAction("spam", spamFolder); else if (msg) { api.post("/mail/messages/move-bulk", { uids: [msg.uid], target: spamFolder }, { params: { folder } }).then(() => { setMsg(null); loadMessages(folder, listing.page); loadFolders(); toast.success("Reported as junk"); }).catch(() => toast.error("Action failed")); } else toast.error("Select a message first"); };
  const ribbonReadUnread = () => { if (hasSel()) bulkAction("seen"); else if (msg) toggleSeen(!msg.seen); else toast.error("Select a message first"); };
  const ribbonFlag = () => { if (hasSel()) bulkAction("flag"); else if (msg) quickAction({ stopPropagation() {} }, msg, "toggleFlag"); else toast.error("Select a message first"); };

  const folderIcon = (f, active) => {
    const Icon = FOLDER_ICONS[f.display] || Folder;
    return <Icon size={16} className={active ? "text-ol-blue" : "text-ol-sub"} />;
  };

  const baseRows = searchInfo ? searchResults : listing.messages;
  const showTabs = folder === "INBOX" && !searchInfo;
  // Focused = real mail (never hides read messages); Other = automated/bulk senders only.
  const isBulk = (m) => /no-?reply|do-?not-?reply|newsletter|notification|mailer|marketing|promo|updates?@|bounce|donotreply|automated/i.test(`${m.from?.email || ""} ${m.from?.name || ""}`);
  const rows = showTabs
    ? baseRows.filter((m) => (inboxTab === "focused" ? !isBulk(m) : isBulk(m)))
    : baseRows;
  const focusedCount = baseRows.filter((m) => !isBulk(m)).length;

  // ---------- Ribbon ----------
  const ribBtn = "flex items-center gap-1.5 px-2.5 h-9 shrink-0 whitespace-nowrap rounded-[4px] text-[13px] text-ol-text hover:bg-ol-hover transition-colors disabled:opacity-40 disabled:hover:bg-transparent";
  const Ribbon = (
    <div className="flex items-center gap-0.5 px-2 h-12 border-b border-ol-line bg-white shrink-0 overflow-x-auto" data-testid="mail-ribbon">
      <button onClick={() => setCompose({})} className="flex items-center gap-2 h-9 pl-3 pr-3.5 rounded-[4px] bg-ol-blue hover:bg-ol-bluehover text-white text-[13px] font-semibold transition-colors shrink-0" data-testid="mail-newmail-button">
        <PenSquare size={15} /> New mail
      </button>
      <span className="w-px h-6 bg-ol-line mx-1.5 shrink-0" />
      <button onClick={ribbonDelete} className={ribBtn} data-testid="ribbon-delete"><Trash2 size={15} className="text-ol-sub" /> Delete</button>
      <button onClick={ribbonArchive} className={ribBtn} data-testid="ribbon-archive"><Archive size={15} className="text-ol-sub" /> Archive</button>
      <button onClick={ribbonReport} className={ribBtn} data-testid="ribbon-report"><ShieldAlert size={15} className="text-ol-sub" /> Report</button>
      <span className="relative shrink-0">
        <button onClick={() => setMoveOpen((o) => !o)} disabled={!hasSel()} className={ribBtn} data-testid="ribbon-moveto"><FolderInput size={15} className="text-ol-sub" /> Move to <ChevronDown size={13} className="text-ol-sub" /></button>
        {moveOpen && hasSel() && (
          <div className="absolute left-0 top-11 z-30 w-48 border border-ol-line bg-white shadow-lg rounded-[4px] py-1" data-testid="ribbon-move-menu">
            {movableFolders.map((f) => (
              <button key={f.name} onClick={() => bulkAction("move", f.name)} className="block w-full text-left px-4 py-2 text-[13px] text-ol-text hover:bg-ol-hover transition-colors">{f.display}</button>
            ))}
          </div>
        )}
      </span>
      <span className="w-px h-6 bg-ol-line mx-1.5 shrink-0" />
      <button onClick={reply} disabled={!msg} className={ribBtn} data-testid="ribbon-reply"><Reply size={15} className="text-ol-sub" /> Reply</button>
      <button onClick={replyAll} disabled={!msg} className={ribBtn} data-testid="ribbon-replyall"><ReplyAll size={15} className="text-ol-sub" /> Reply all</button>
      <button onClick={forward} disabled={!msg} className={ribBtn} data-testid="ribbon-forward"><Forward size={15} className="text-ol-sub" /> Forward</button>
      <span className="w-px h-6 bg-ol-line mx-1.5 shrink-0" />
      <button onClick={ribbonReadUnread} className={ribBtn} data-testid="ribbon-readunread"><MailOpen size={15} className="text-ol-sub" /> Read / Unread</button>
      <button onClick={ribbonFlag} className={ribBtn} data-testid="ribbon-flag"><Flag size={15} className="text-ol-sub" /> Flag</button>
      <span className="w-px h-6 bg-ol-line mx-1.5 shrink-0" />
      <button onClick={(e) => { if (!msg) return toast.error("Open a message first"); setPopover({ kind: "tag", m: msg, x: e.clientX, y: e.clientY }); }} disabled={!msg} className={ribBtn} data-testid="ribbon-categorize"><Tag size={15} className="text-ol-sub" /> Categorize</button>
      <button onClick={(e) => { if (!msg) return toast.error("Open a message first"); setPopover({ kind: "snooze", m: msg, x: e.clientX, y: e.clientY }); }} disabled={!msg} className={ribBtn} data-testid="ribbon-snooze"><Clock3 size={15} className="text-ol-sub" /> Snooze</button>
      <button onClick={() => togglePin(msg?.message_id)} disabled={!msg} className={ribBtn} data-testid="ribbon-pin"><Pin size={15} className={msg && pins.has(msg.message_id) ? "text-ol-blue" : "text-ol-sub"} fill={msg && pins.has(msg.message_id) ? "currentColor" : "none"} /> {msg && pins.has(msg.message_id) ? "Unpin" : "Pin"}</button>
      <button onClick={printMessage} disabled={!msg} className={ribBtn} data-testid="ribbon-print"><Printer size={15} className="text-ol-sub" /> Print</button>
    </div>
  );

  const DEFAULT_FOLDERS = [
    { name: "INBOX", display: "Inbox", unseen: 0 },
    { name: "Sent", display: "Sent", unseen: 0 },
    { name: "Drafts", display: "Drafts", unseen: 0 },
    { name: "Junk", display: "Spam", unseen: 0 },
    { name: "Trash", display: "Trash", unseen: 0 },
    { name: "Archive", display: "Archive", unseen: 0 },
  ];
  const folderTemplate = folders.length ? folders : DEFAULT_FOLDERS;

  const toggleAccountCollapse = (email) =>
    setCollapsedAccounts((s) => { const n = new Set(s); n.has(email) ? n.delete(email) : n.add(email); return n; });

  const openAccountFolder = async (email, folderName) => {
    if (email === activeEmail) { switchFolder(folderName); return; }
    await switchAccount(email);
    if (folderName && folderName !== "INBOX") switchFolder(folderName);
  };

  // Outlook-style: every mailbox lists its folders directly underneath, always visible.
  const accountRow = (a) => {
    const slug = a.email.replace(/[^a-z0-9]/g, "-");
    const isOpen = !collapsedAccounts.has(a.email);
    return (
      <div key={a.email} data-testid={`mail-account-block-${slug}`}>
        <div className={`group flex items-center gap-0.5 pl-1.5 pr-2 py-1.5 border-l-2 ${a.active ? "border-ol-blue bg-ol-sel" : "border-transparent hover:bg-ol-hover"}`}>
          <button onClick={() => toggleAccountCollapse(a.email)} className="text-ol-sub hover:text-ol-blue shrink-0 p-0.5" aria-label={isOpen ? "Collapse mailbox" : "Expand mailbox"} data-testid={`mail-account-expand-${slug}`}>
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <button onClick={() => switchAccount(a.email)} className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`mail-account-${slug}`} title={`Switch to ${a.email}`}>
            <span className={`truncate text-[13px] ${a.active ? "text-ol-blue font-bold" : "text-ol-text font-medium"}`}>{a.email}</span>
            {a.active && <span className="text-[9px] uppercase tracking-wide bg-ol-blue text-white px-1.5 py-0.5 rounded-full shrink-0" data-testid={`mail-account-current-${slug}`}>current</span>}
            {!a.active && a.unseen > 0 && <span className="ml-auto text-[11px] font-semibold text-ol-blue shrink-0">{a.unseen}</span>}
          </button>
          {!a.primary && (
            <button onClick={() => removeAccount(a.email)} className="opacity-0 group-hover:opacity-100 text-ol-sub hover:text-red-500 transition-all shrink-0" title="Remove from this sign-in" aria-label={`Remove ${a.email}`} data-testid={`mail-account-remove-${slug}`}>
              <X size={13} />
            </button>
          )}
        </div>
        {isOpen && folderTemplate.map((f) => {
          const fActive = a.active && folder === f.name;
          const count = a.active ? f.unseen : (f.name === "INBOX" ? a.unseen : 0);
          return (
            <button key={f.name} onClick={() => openAccountFolder(a.email, f.name)}
              className={`relative flex items-center gap-2.5 w-full pl-8 pr-3 py-1.5 text-[13px] transition-colors ${fActive ? "bg-ol-sel text-ol-blue font-semibold" : "text-ol-text hover:bg-ol-hover"}`}
              data-testid={`mail-acct-folder-${slug}-${f.display.toLowerCase().replace(/\s+/g, "-")}`}>
              {fActive && <span className="absolute left-3 top-1.5 bottom-1.5 w-[3px] rounded-full bg-ol-blue" />}
              {folderIcon(f, fActive)}
              <span className="truncate">{f.display}</span>
              {count > 0 && <span className={`ml-auto text-[12px] font-semibold ${fActive ? "text-ol-blue" : "text-ol-sub"}`}>{count}</span>}
            </button>
          );
        })}
        {isOpen && (
          <button onClick={() => openAccountFolder(a.email, "__scheduled")}
            className={`relative flex items-center gap-2.5 w-full pl-8 pr-3 py-1.5 text-[13px] transition-colors ${a.active && folder === "__scheduled" ? "bg-ol-sel text-ol-blue font-semibold" : "text-ol-text hover:bg-ol-hover"}`}
            data-testid={`mail-acct-folder-${slug}-scheduled`}>
            {a.active && folder === "__scheduled" && <span className="absolute left-3 top-1.5 bottom-1.5 w-[3px] rounded-full bg-ol-blue" />}
            <CalendarClock size={16} className={a.active && folder === "__scheduled" ? "text-ol-blue" : "text-ol-sub"} />
            <span className="truncate">Scheduled</span>
            {scheduled.filter((s) => s.status === "pending" && s.email === a.email).length > 0 && <span className="ml-auto text-[12px] font-semibold text-ol-sub" data-testid={`mail-scheduled-count-${slug}`}>{scheduled.filter((s) => s.status === "pending" && s.email === a.email).length}</span>}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-white text-ol-text" style={OUTLOOK_FONT} data-testid="mail-app">
      {/* Top bar — Outlook blue */}
      <header className="flex items-center h-12 px-3 bg-ol-blue text-white shrink-0" data-testid="mail-topbar">
        <button onClick={() => setSidebarOpen((o) => !o)} className="p-2 hover:bg-white/10 rounded-[4px] transition-colors md:mr-1" aria-label="Toggle folders" data-testid="mail-hamburger">
          <Menu size={18} />
        </button>
        <span className="text-[17px] font-semibold tracking-tight mr-4 hidden sm:block">Outlook</span>
        <form onSubmit={runSearch} className="flex-1 max-w-2xl mx-auto flex items-center gap-2 bg-white/95 rounded-[4px] px-3 h-8" data-testid="mail-search-form">
          <Search size={15} className="text-ol-sub shrink-0" />
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search"
            className="flex-1 min-w-0 bg-transparent text-[13px] text-ol-text placeholder:text-ol-sub focus:outline-none"
            data-testid="mail-search-input"
          />
          {searchBusy && <Loader2 size={14} className="animate-spin text-ol-sub" />}
          {searchInfo && (
            <button type="button" onClick={clearSearch} className="text-ol-sub hover:text-ol-text" aria-label="Clear search" data-testid="mail-search-clear"><X size={14} /></button>
          )}
        </form>
        <div className="flex items-center gap-0.5 ml-4">
          <button onClick={() => { searchInfo ? runSearch() : loadMessages(folder, listing.page); loadFolders(); loadAccounts(); }} className="p-2 hover:bg-white/10 rounded-[4px] transition-colors" aria-label="Refresh" title="Refresh" data-testid="mail-refresh"><RefreshCw size={17} /></button>
          <button onClick={() => setSigModal(true)} className="p-2 hover:bg-white/10 rounded-[4px] transition-colors hidden sm:inline-flex" aria-label="My mail signature" title="My mail signature" data-testid="mail-signature-button"><PenLine size={17} /></button>
          <button className="p-2 hover:bg-white/10 rounded-[4px] transition-colors hidden sm:inline-flex" aria-label="Notifications" title="Notifications" data-testid="mail-bell"><Bell size={17} /></button>
          <button onClick={() => setSettingsOpen(true)} className="p-2 hover:bg-white/10 rounded-[4px] transition-colors" aria-label="Settings" title="Settings" data-testid="mail-settings-button"><Settings size={17} /></button>
          <button onClick={() => setProfileModal(true)} className="ml-1 shrink-0 rounded-full hover:ring-2 hover:ring-white/50 transition-shadow" aria-label="My profile" title={activeEmail} data-testid="mail-avatar-button">
            {profile?.avatar ? (
              <img src={profile.avatar} alt="" className="h-8 w-8 rounded-full object-cover" data-testid="mail-header-avatar" />
            ) : (
              <span className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-[13px] font-semibold" data-testid="mail-header-avatar">{(activeEmail || "?")[0].toUpperCase()}</span>
            )}
          </button>
          <button onClick={onLogout} className="p-2 hover:bg-white/10 rounded-[4px] transition-colors" aria-label="Sign out" title="Sign out" data-testid="mail-logout"><LogOut size={16} /></button>
        </div>
      </header>

      {appView === "mail" && Ribbon}

      {/* mobile app switcher */}
      <div className="md:hidden flex items-center border-b border-ol-line bg-white shrink-0" data-testid="mail-appnav-mobile">
        {APP_NAV.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setAppView(id)} className={`flex-1 flex flex-col items-center gap-1 py-2 text-[10px] transition-colors ${appView === id ? "text-ol-blue border-b-2 border-ol-blue" : "text-ol-sub"}`} data-testid={`appnav-${id}`}>
            <Icon size={17} /> {label}
          </button>
        ))}
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Outlook app rail */}
        <nav className="hidden md:flex flex-col items-center gap-1 w-12 shrink-0 bg-ol-bg border-r border-ol-line py-2" data-testid="mail-apprail">
          {APP_NAV.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setAppView(id)} title={label} aria-label={label}
              className={`relative flex items-center justify-center w-10 h-10 rounded-[4px] transition-colors ${appView === id ? "text-ol-blue bg-ol-sel" : "text-ol-sub hover:bg-ol-hover"}`}
              data-testid={`apprail-${id}`}>
              {appView === id && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-ol-blue" />}
              <Icon size={20} />
            </button>
          ))}
        </nav>

        {appView !== "mail" ? (
          <main className="flex-1 min-w-0 min-h-0 bg-white" key={activeEmail} data-testid="mail-app-content">
            {appView === "calendar" && <CalendarView api={api} contacts={contacts} accounts={accounts} activeEmail={activeEmail} onSwitchAccount={switchAccount} />}
            {appView === "people" && <ContactsView api={api} onCompose={(to) => { setAppView("mail"); setCompose({ to }); }} />}
            {appView === "todo" && <TasksView api={api} />}
          </main>
        ) : (
          <>
            {/* folder sidebar */}
            {sidebarOpen && (
              <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-ol-line bg-ol-bg overflow-y-auto" data-testid="mail-folders">
                <p className="px-4 pt-4 pb-2 text-[12px] font-semibold text-ol-sub" data-testid="mail-accounts">Mailboxes</p>
                {accounts.map(accountRow)}
                {canAddAccount && (
                  <button onClick={() => setAddAccountOpen(true)} className="flex items-center gap-2 px-4 py-2 mt-1 mb-4 text-[13px] text-ol-blue hover:bg-ol-hover transition-colors" data-testid="mail-add-account">
                    <MailPlus size={15} /> Add account
                  </button>
                )}
              </aside>
            )}

            {/* message list */}
            <section className={`w-full md:w-[360px] shrink-0 border-r border-ol-line flex-col min-h-0 bg-white ${mobileReading ? "hidden md:flex" : "flex"}`} data-testid="mail-list-pane">
              {/* current mailbox indicator — always shows which mail id you're viewing */}
              <div className="hidden md:flex items-center gap-2 px-4 py-2 border-b border-ol-line bg-ol-sel/60 shrink-0" data-testid="mail-current-mailbox">
                <span className="w-2 h-2 rounded-full bg-ol-blue shrink-0" />
                <span className="text-[13px] font-bold text-ol-blue truncate" data-testid="mail-current-mailbox-email">{activeEmail}</span>
                <span className="text-ol-sub">›</span>
                <span className="text-[13px] text-ol-sub truncate">{folder === "__scheduled" ? "Scheduled" : (folderTemplate.find((f) => f.name === folder)?.display || folder)}</span>
              </div>
              {/* mobile selectors */}
              <div className="md:hidden px-3 pt-2 pb-2 border-b border-ol-line flex flex-col gap-2">
                {accounts.length > 1 && (
                  <select value={activeEmail} onChange={(e) => switchAccount(e.target.value)} className="w-full border border-ol-line rounded-[4px] text-ol-text px-3 py-2 text-[13px] focus:outline-none focus:border-ol-blue" data-testid="mail-account-select">
                    {accounts.map((a) => (<option key={a.email} value={a.email}>{a.email}{a.unseen > 0 ? ` (${a.unseen})` : ""}</option>))}
                  </select>
                )}
                <select value={folder} onChange={(e) => switchFolder(e.target.value)} className="w-full border border-ol-line rounded-[4px] text-ol-text px-3 py-2 text-[13px] focus:outline-none focus:border-ol-blue" data-testid="mail-folder-select">
                  {folders.map((f) => (<option key={f.name} value={f.name}>{f.display}{f.unseen > 0 ? ` (${f.unseen})` : ""}</option>))}
                  <option value="__scheduled">Scheduled</option>
                </select>
              </div>

              {folder !== "__scheduled" && !searchInfo && (
                <div className="flex items-center justify-between px-3 pt-2 border-b border-ol-line" data-testid="mail-list-header">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      title="Select all"
                      aria-label="Select all messages"
                      className="accent-ol-blue w-4 h-4 cursor-pointer mb-1.5 shrink-0"
                      checked={rows.length > 0 && rows.every((m) => selected.has(m.uid))}
                      ref={(el) => { if (el) el.indeterminate = rows.some((m) => selected.has(m.uid)) && !rows.every((m) => selected.has(m.uid)); }}
                      onChange={(e) => setSelected((s) => { const n = new Set(s); if (e.target.checked) rows.forEach((m) => n.add(m.uid)); else rows.forEach((m) => n.delete(m.uid)); return n; })}
                      data-testid="mail-select-all"
                    />
                  {showTabs ? (
                    <div className="flex items-center gap-4">
                      <button onClick={() => setInboxTab("focused")} className={`relative pb-2 text-[13px] transition-colors ${inboxTab === "focused" ? "text-ol-blue font-semibold" : "text-ol-sub hover:text-ol-text"}`} data-testid="mail-tab-focused">
                        Focused
                        {inboxTab === "focused" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-ol-blue rounded-full" />}
                      </button>
                      <button onClick={() => setInboxTab("other")} className={`relative pb-2 text-[13px] transition-colors ${inboxTab === "other" ? "text-ol-blue font-semibold" : "text-ol-sub hover:text-ol-text"}`} data-testid="mail-tab-other">
                        Other
                        {inboxTab === "other" && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-ol-blue rounded-full" />}
                      </button>
                    </div>
                  ) : (
                    <span className="pb-2 text-[13px] font-semibold text-ol-text">{folders.find((f) => f.name === folder)?.display || ""}</span>
                  )}
                  </div>
                  <div className="flex items-center gap-0.5 pb-1.5">
                    {(folders.find((f) => f.name === folder)?.unseen || 0) > 0 && (
                      <button onClick={markAllRead} disabled={bulkBusy} className="p-1.5 text-ol-sub hover:bg-ol-hover rounded-[4px] transition-colors disabled:opacity-50" title="Mark all as read" data-testid="mail-mark-all-read"><MailCheck size={16} /></button>
                    )}
                    <span className="relative">
                      <button onClick={() => setSortOpen((o) => !o)} className="p-1.5 text-ol-sub hover:bg-ol-hover rounded-[4px] transition-colors" title="Filter & sort" data-testid="mail-sort-button"><ArrowUpDown size={16} /></button>
                      {sortOpen && (
                        <div className="absolute right-0 top-9 z-30 w-52 border border-ol-line bg-white shadow-lg rounded-[4px] py-1" data-testid="mail-sort-menu">
                          <p className="px-3 py-1 text-[11px] font-semibold text-ol-sub uppercase tracking-wide">Filter</p>
                          {["all", "unread", "flagged"].map((f) => (
                            <button key={f} onClick={() => { applyFilter(f); setSortOpen(false); }} className={`block w-full text-left px-4 py-1.5 text-[13px] hover:bg-ol-hover transition-colors ${listFilter === f ? "text-ol-blue font-semibold" : "text-ol-text"}`} data-testid={`mail-filter-${f}`}>
                              {f === "all" ? "All" : f === "unread" ? "Unread" : "Flagged"}
                            </button>
                          ))}
                          <p className="px-3 py-1 mt-1 text-[11px] font-semibold text-ol-sub uppercase tracking-wide border-t border-ol-line">Sort by</p>
                          {SORT_OPTIONS.map(([v, label]) => (
                            <button key={v} onClick={() => applySort(v)} className={`block w-full text-left px-4 py-1.5 text-[13px] hover:bg-ol-hover transition-colors ${sortBy === v ? "text-ol-blue font-semibold" : "text-ol-text"}`} data-testid={`mail-sort-${v}`}>{label}</button>
                          ))}
                        </div>
                      )}
                    </span>
                  </div>
                </div>
              )}

              {searchInfo && (
                <p className="px-4 py-2 text-[12px] text-ol-blue border-b border-ol-line" data-testid="mail-search-banner">
                  {searchInfo.total} result{searchInfo.total === 1 ? "" : "s"} for “{searchInfo.q}”
                  {searchInfo.total > searchResults.length ? ` — showing newest ${searchResults.length}` : ""} · <button className="underline" onClick={clearSearch}>Clear</button>
                </p>
              )}

              {/* bulk action bar */}
              {(selected.size > 0 || allFolder) && folder !== "__scheduled" && (
                <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-ol-line bg-ol-sel" data-testid="mail-bulk-actions">
                  <span className="text-[12px] text-ol-text mr-2">{allFolder ? listing.total : selected.size} selected</span>
                  <button disabled={bulkBusy} title="Mark read" onClick={() => bulkAction("seen")} className="p-1.5 text-ol-sub hover:bg-white rounded-[4px] transition-colors disabled:opacity-50" data-testid="mail-mark-read"><MailOpen size={15} /></button>
                  <button disabled={bulkBusy} title="Mark unread" onClick={() => bulkAction("unseen")} className="p-1.5 text-ol-sub hover:bg-white rounded-[4px] transition-colors disabled:opacity-50" data-testid="mail-mark-unread"><Mail size={15} /></button>
                  <button disabled={bulkBusy} title="Flag" onClick={() => bulkAction("flag")} className="p-1.5 text-ol-sub hover:bg-white rounded-[4px] transition-colors disabled:opacity-50" data-testid="mail-flag"><Flag size={15} /></button>
                  <button disabled={bulkBusy} title="Archive" onClick={() => bulkAction("archive")} className="p-1.5 text-ol-sub hover:bg-white rounded-[4px] transition-colors disabled:opacity-50" data-testid="mail-archive"><Archive size={15} /></button>
                  {spamFolder && folder !== spamFolder && (
                    <button disabled={bulkBusy} title="Junk" onClick={() => bulkAction("spam", spamFolder)} className="p-1.5 text-ol-sub hover:bg-white rounded-[4px] transition-colors disabled:opacity-50" data-testid="mail-spam"><ShieldAlert size={15} /></button>
                  )}
                  <button disabled={bulkBusy} title="Delete" onClick={() => bulkAction("delete")} className="p-1.5 text-ol-sub hover:bg-white hover:text-red-500 rounded-[4px] transition-colors disabled:opacity-50" data-testid="mail-delete-bulk"><Trash2 size={15} /></button>
                  <button onClick={() => { setSelected(new Set()); setAllFolder(false); }} className="ml-auto text-[12px] text-ol-blue hover:underline">Clear</button>
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto" data-testid="mail-list-scroll">
                {folder === "__scheduled" ? (
                  (() => { const sched = scheduled.filter((s) => s.email === activeEmail); return (
                  sched.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-16 text-ol-sub" data-testid="mail-scheduled-empty">
                      <CalendarClock size={28} strokeWidth={1.2} />
                      <p className="text-[13px]">Nothing scheduled</p>
                      <p className="text-[12px] px-8 text-center">In Compose, choose “Send later…” and the mail goes out automatically. You get an alert when it sends — or if it fails.</p>
                    </div>
                  ) : (
                    sched.map((s) => (
                      <div key={s.id} className="px-4 py-3 border-b border-ol-line" data-testid={`mail-scheduled-${s.id}`}>
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="truncate text-[13px] text-ol-text font-semibold">{s.to}</span>
                          <span className={`text-[11px] uppercase tracking-wide shrink-0 ${s.status === "pending" ? "text-ol-blue" : s.status === "sent" ? "text-green-600" : s.status === "failed" ? "text-red-500" : "text-ol-sub"}`} data-testid={`mail-scheduled-status-${s.id}`}>{s.status}</span>
                        </div>
                        <p className="mt-0.5 truncate text-[12px] text-ol-sub">{s.subject || "(no subject)"}</p>
                        <div className="mt-1.5 flex items-center justify-between gap-3">
                          <span className="text-[11px] text-ol-sub">{new Date(s.send_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          {s.status === "pending" && (
                            <div className="flex items-center gap-3">
                              <button onClick={() => setCompose({ to: s.to, cc: s.cc, bcc: s.bcc, subject: s.subject, body_html: s.body_html, editScheduledId: s.id, sendAtInit: s.send_at })} className="text-[11px] uppercase tracking-wide text-ol-blue hover:text-ol-bluehover transition-colors" data-testid={`mail-scheduled-edit-${s.id}`}>Edit</button>
                              <button onClick={() => cancelScheduled(s.id)} className="text-[11px] uppercase tracking-wide text-red-500 hover:text-red-600 transition-colors" data-testid={`mail-scheduled-cancel-${s.id}`}>Cancel</button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )
                  ); })()
                ) : listLoading && !searchInfo ? (
                  <div className="flex items-center justify-center gap-2 py-16 text-ol-sub text-[13px]" data-testid="mail-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>
                ) : rows.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 py-20 text-ol-sub" data-testid="mail-empty">
                    <MailCheck size={30} strokeWidth={1.2} className="text-ol-blue/50" />
                    <p className="text-[14px] font-semibold text-ol-text">{searchInfo ? "No matches" : showTabs && inboxTab === "focused" ? "You're all caught up" : "Nothing here"}</p>
                    <p className="text-[12px]">{showTabs ? (inboxTab === "focused" ? "Enjoy your empty Focused inbox." : "No other messages.") : "No messages in this folder."}</p>
                  </div>
                ) : (
                  [...buildThreads(rows)]
                    .sort((a, b) => (a.messages.some((x) => pins.has(x.message_id)) ? 0 : 1) - (b.messages.some((x) => pins.has(x.message_id)) ? 0 : 1))
                    .map((th) => {
                    const m = th.latest;
                    const isThread = th.count > 1;
                    const isOpen = expanded.has(th.key);
                    const active = th.messages.some((x) => msg?.uid === x.uid) || conversation?.key === th.key;
                    const chosen = th.messages.length > 0 && th.messages.every((x) => selected.has(x.uid));
                    return (
                      <div key={th.key} data-testid={`mail-conversation-${m.uid}`}>
                        <div
                          className={`group relative flex items-stretch border-b border-ol-line/70 transition-colors cursor-pointer ${active ? "bg-ol-sel" : chosen ? "bg-ol-sel/60" : "hover:bg-ol-hover"}`}
                          onClick={() => (isThread ? openConversation(th) : openMessage(m))}
                          onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ m, x: e.clientX, y: e.clientY }); setCtxMoveOpen(false); }}
                          data-testid={`mail-message-${m.uid}`}
                        >
                          {th.unseen && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-ol-blue" />}
                          <span className="flex items-center pl-3" onClick={(e) => { e.stopPropagation(); selectThread(th.messages); }}>
                            <input type="checkbox" className="accent-ol-blue w-3.5 h-3.5 cursor-pointer" checked={chosen} onChange={() => {}} aria-label="Select conversation" data-testid={`mail-select-${m.uid}`} />
                          </span>
                          <div className="flex-1 min-w-0 flex items-start gap-3 pr-3 pl-2.5 py-2.5">
                            {isThread ? (
                              <button onClick={(e) => { e.stopPropagation(); toggleThread(th.key); }} className="mt-1 text-ol-sub hover:text-ol-blue shrink-0" aria-label={isOpen ? "Collapse conversation" : "Expand conversation"} data-testid={`mail-thread-toggle-${m.uid}`}>
                                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </button>
                            ) : (
                              <SenderAvatar email={m.from?.email} name={m.from?.name} size={32} />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2">
                                <span className={`truncate text-[13px] ${th.unseen ? "text-ol-text font-bold" : "text-ol-text"}`}>
                                  {m.from?.name || m.from?.email || "Unknown"}
                                  {isThread && <span className="ml-1.5 text-[11px] font-semibold text-ol-blue" data-testid={`mail-thread-count-${m.uid}`}>({th.count})</span>}
                                </span>
                                <span className="text-[11px] text-ol-sub shrink-0 group-hover:hidden">{fmtDay(m.date)}</span>
                              </div>
                              <div className="flex items-center">
                                <CategoryDots ids={assignments[m.message_id]} categories={categories} />
                                <span className={`truncate text-[13px] ${th.unseen ? "text-ol-blue font-semibold" : "text-ol-text"}`}>{m.subject}</span>
                              </div>
                            </div>
                            {th.messages.some((x) => pins.has(x.message_id)) && <Pin size={12} className="text-ol-blue shrink-0 mt-0.5" fill="currentColor" data-testid={`mail-pinned-${m.uid}`} />}
                            {th.flagged && <Flag size={13} className="text-red-500 shrink-0 mt-0.5" fill="currentColor" />}
                          </div>
                          {/* hover quick actions (act on latest message) */}
                          <div className="hidden group-hover:flex absolute right-2 top-2 items-center gap-0.5 bg-white shadow-sm border border-ol-line rounded-full px-1 py-0.5" onClick={(e) => e.stopPropagation()}>
                            <span role="button" tabIndex={0} title="Snooze" onClick={(e) => setPopover({ kind: "snooze", m, x: e.clientX, y: e.clientY })} className="p-1.5 text-ol-sub hover:text-ol-blue transition-colors" data-testid={`mail-quick-snooze-${m.uid}`}><Clock3 size={14} /></span>
                            <span role="button" tabIndex={0} title="Categories" onClick={(e) => setPopover({ kind: "tag", m, x: e.clientX, y: e.clientY })} className="p-1.5 text-ol-sub hover:text-ol-blue transition-colors" data-testid={`mail-quick-tag-${m.uid}`}><Tag size={14} /></span>
                            <span role="button" tabIndex={0} title={m.seen ? "Mark unread" : "Mark read"} onClick={(e) => quickAction(e, m, "toggleSeen")} className="p-1.5 text-ol-sub hover:text-ol-blue transition-colors" data-testid={`mail-quick-seen-${m.uid}`}>{m.seen ? <Mail size={14} /> : <MailOpen size={14} />}</span>
                            <span role="button" tabIndex={0} title={m.flagged ? "Unflag" : "Flag"} onClick={(e) => quickAction(e, m, "toggleFlag")} className={`p-1.5 transition-colors ${m.flagged ? "text-red-500" : "text-ol-sub hover:text-ol-blue"}`} data-testid={`mail-quick-flag-${m.uid}`}><Flag size={14} fill={m.flagged ? "currentColor" : "none"} /></span>
                            <span role="button" tabIndex={0} title="Archive" onClick={(e) => quickAction(e, m, "archive")} className="p-1.5 text-ol-sub hover:text-ol-blue transition-colors" data-testid={`mail-quick-archive-${m.uid}`}><Archive size={14} /></span>
                            <span role="button" tabIndex={0} title="Delete" onClick={(e) => quickAction(e, m, "delete")} className="p-1.5 text-ol-sub hover:text-red-500 transition-colors" data-testid={`mail-quick-delete-${m.uid}`}><Trash2 size={14} /></span>
                          </div>
                        </div>
                        {/* expanded conversation children */}
                        {isThread && isOpen && th.messages.map((cm) => (
                          <div
                            key={cm.uid}
                            className={`group relative flex items-stretch border-b border-ol-line/60 pl-8 transition-colors cursor-pointer ${msg?.uid === cm.uid ? "bg-ol-sel" : "hover:bg-ol-hover"}`}
                            onClick={() => openMessage(cm)}
                            onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ m: cm, x: e.clientX, y: e.clientY }); setCtxMoveOpen(false); }}
                            data-testid={`mail-message-${cm.uid}`}
                          >
                            {!cm.seen && <span className="absolute left-6 top-0 bottom-0 w-[3px] bg-ol-blue" />}
                            <div className="flex-1 min-w-0 flex items-start gap-3 pr-3 pl-2.5 py-2">
                              <SenderAvatar email={cm.from?.email} name={cm.from?.name} size={26} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                  <span className={`truncate text-[12px] ${cm.seen ? "text-ol-text" : "text-ol-text font-bold"}`}>{cm.from?.name || cm.from?.email || "Unknown"}</span>
                                  <span className="text-[11px] text-ol-sub shrink-0 group-hover:hidden">{fmtDay(cm.date)}</span>
                                </div>
                              </div>
                              {cm.flagged && <Flag size={12} className="text-red-500 shrink-0 mt-0.5" fill="currentColor" />}
                            </div>
                            <div className="hidden group-hover:flex absolute right-2 top-1.5 items-center gap-0.5 bg-white shadow-sm border border-ol-line rounded-full px-1 py-0.5" onClick={(e) => e.stopPropagation()}>
                              <span role="button" tabIndex={0} title={cm.seen ? "Mark unread" : "Mark read"} onClick={(e) => quickAction(e, cm, "toggleSeen")} className="p-1.5 text-ol-sub hover:text-ol-blue transition-colors" data-testid={`mail-quick-seen-${cm.uid}`}>{cm.seen ? <Mail size={13} /> : <MailOpen size={13} />}</span>
                              <span role="button" tabIndex={0} title={cm.flagged ? "Unflag" : "Flag"} onClick={(e) => quickAction(e, cm, "toggleFlag")} className={`p-1.5 transition-colors ${cm.flagged ? "text-red-500" : "text-ol-sub hover:text-ol-blue"}`} data-testid={`mail-quick-flag-${cm.uid}`}><Flag size={13} fill={cm.flagged ? "currentColor" : "none"} /></span>
                              <span role="button" tabIndex={0} title="Delete" onClick={(e) => quickAction(e, cm, "delete")} className="p-1.5 text-ol-sub hover:text-red-500 transition-colors" data-testid={`mail-quick-delete-${cm.uid}`}><Trash2 size={13} /></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>

              {!searchInfo && listing.pages > 1 && folder !== "__scheduled" && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-ol-line text-[12px] text-ol-sub shrink-0" data-testid="mail-pagination">
                  <button disabled={listing.page <= 1} onClick={() => loadMessages(folder, listing.page - 1)} className="flex items-center gap-1 hover:text-ol-blue disabled:opacity-40 transition-colors" data-testid="mail-prev-page"><ChevronLeft size={14} /> Newer</button>
                  <span>{listing.page} / {listing.pages}</span>
                  <button disabled={listing.page >= listing.pages} onClick={() => loadMessages(folder, listing.page + 1)} className="flex items-center gap-1 hover:text-ol-blue disabled:opacity-40 transition-colors" data-testid="mail-next-page">Older <ChevronRight size={14} /></button>
                </div>
              )}
            </section>

            {/* reading pane */}
            <main className={`flex-1 min-w-0 min-h-0 bg-white ${mobileReading ? "flex" : "hidden md:flex"} flex-col`} data-testid="mail-reading-pane">
              {msgLoading ? (
                <div className="flex-1 flex items-center justify-center gap-2 text-ol-sub text-[13px]"><Loader2 size={16} className="animate-spin" /> Opening…</div>
              ) : conversation ? (
                <ConversationView
                  api={api}
                  folder={folder}
                  thread={conversation}
                  activeEmail={activeEmail}
                  onBack={() => { setMobileReading(false); setConversation(null); }}
                  onForward={forwardMessage}
                  onSeen={markRead}
                  onSent={afterReplySent}
                  onUndoableSend={(u) => setUndoSend(u)}
                  onDeleteLatest={() => quickAction({ stopPropagation() {} }, conversation.messages[0], "delete")}
                />
              ) : msg ? (
                <MessageView api={api} msg={msg} busy={actionBusy} activeEmail={activeEmail} onBack={() => { setMobileReading(false); setMsg(null); }} onReply={reply} onReplyAll={replyAll} onForward={forward} onDelete={removeMessage} onToggleSeen={toggleSeen} onSent={afterReplySent} onUndoableSend={(u) => setUndoSend(u)} />
              ) : (
                <div className="flex-1 hidden md:flex flex-col items-center justify-center gap-3 text-ol-sub" data-testid="mail-placeholder">
                  <div className="w-24 h-20 rounded-lg bg-ol-sel flex items-center justify-center"><Mail size={40} strokeWidth={1.2} className="text-ol-blue/60" /></div>
                  <p className="text-[15px] font-semibold text-ol-text">All done for the day</p>
                  <p className="text-[13px]">Select a message to read it here.</p>
                </div>
              )}
            </main>
          </>
        )}
      </div>

      {/* right-click context menu (Outlook-style) */}
      {ctxMenu && (
        <div
          className="fixed z-[60] w-52 bg-white border border-ol-line shadow-xl rounded-[6px] py-1 text-[13px]"
          style={{ left: Math.min(ctxMenu.x, window.innerWidth - 220), top: Math.min(ctxMenu.y, window.innerHeight - 300) }}
          onClick={(e) => e.stopPropagation()}
          data-testid="mail-context-menu"
        >
          <button onClick={() => ctxAction("toggleSeen")} className="flex items-center gap-2.5 w-full text-left px-4 py-2 text-ol-text hover:bg-ol-hover transition-colors" data-testid="ctx-read">
            {ctxMenu.m.seen ? <Mail size={15} className="text-ol-sub" /> : <MailOpen size={15} className="text-ol-sub" />} {ctxMenu.m.seen ? "Mark as unread" : "Mark as read"}
          </button>
          <button onClick={() => ctxAction("toggleFlag")} className="flex items-center gap-2.5 w-full text-left px-4 py-2 text-ol-text hover:bg-ol-hover transition-colors" data-testid="ctx-flag">
            <Flag size={15} className={ctxMenu.m.flagged ? "text-red-500" : "text-ol-sub"} /> {ctxMenu.m.flagged ? "Clear flag" : "Flag"}
          </button>
          <button onClick={() => ctxAction("archive")} className="flex items-center gap-2.5 w-full text-left px-4 py-2 text-ol-text hover:bg-ol-hover transition-colors" data-testid="ctx-archive">
            <Archive size={15} className="text-ol-sub" /> Archive
          </button>
          <div className="relative" onMouseEnter={() => setCtxMoveOpen(true)} onMouseLeave={() => setCtxMoveOpen(false)}>
            <button className="flex items-center justify-between gap-2.5 w-full text-left px-4 py-2 text-ol-text hover:bg-ol-hover transition-colors" data-testid="ctx-move">
              <span className="flex items-center gap-2.5"><FolderInput size={15} className="text-ol-sub" /> Move to</span>
              <ChevronRight size={14} className="text-ol-sub" />
            </button>
            {ctxMoveOpen && (
              <div className="absolute top-0 right-full mr-0 w-44 max-h-64 overflow-y-auto bg-white border border-ol-line shadow-xl rounded-[6px] py-1" data-testid="ctx-move-menu">
                {movableFolders.map((f) => (
                  <button key={f.name} onClick={() => ctxAction("move", f.display)} className="block w-full text-left px-4 py-2 text-ol-text hover:bg-ol-hover transition-colors" data-testid={`ctx-move-${f.display.toLowerCase().replace(/\s+/g, "-")}`}>{f.display}</button>
                ))}
              </div>
            )}
          </div>
          <div className="h-px bg-ol-line my-1" />
          <button onClick={() => ctxAction("delete")} className="flex items-center gap-2.5 w-full text-left px-4 py-2 text-red-500 hover:bg-ol-hover transition-colors" data-testid="ctx-delete">
            <Trash2 size={15} /> Delete
          </button>
        </div>
      )}

      {popover?.kind === "snooze" && <SnoozePopover pos={popover} onClose={() => setPopover(null)} onPick={(iso) => snoozeMessage(popover.m, iso)} />}
      {popover?.kind === "tag" && (
        <CategoryPopover pos={popover} api={api} messageId={popover.m.message_id} categories={categories} assigned={assignments[popover.m.message_id] || []} onChanged={(mid, ids) => setAssignments((a) => ({ ...a, [mid]: ids }))} onCategoriesChanged={(cats) => setCategories(cats)} onClose={() => setPopover(null)} />
      )}
      {undoSend && <UndoSendBar undo={undoSend} onUndo={undoNow} onDone={() => { setUndoSend(null); toast.success("Email sent"); }} />}
      {compose && (
        <ComposeModal api={api} draft={compose} hasSignature={hasSignature} draftsFolder={draftsFolder} contacts={contacts} accounts={accounts} activeEmail={activeEmail} onClose={() => setCompose(null)} onUndoableSend={(u) => setUndoSend(u)} onSent={() => folder.toLowerCase().startsWith("sent") && loadMessages(folder, 1)} />
      )}
      {sigModal && <SignatureModal api={api} user={{ email: activeEmail }} accounts={accounts} onClose={() => setSigModal(false)} onSaved={() => { setHasSignature(true); loadSignatureFlag(); }} />}
      {profileModal && <ProfileModal api={api} profile={profile || { email: activeEmail, role: "staff", avatar: null, avatar_locked: false }} onClose={() => setProfileModal(false)} onUpdated={loadProfile} />}
      {addAccountOpen && (
        <AddAccountModal api={api} onClose={() => setAddAccountOpen(false)} onAdded={() => { loadAccounts().then(() => { setMsg(null); setSearchInfo(null); setSearchQ(""); setFolder("INBOX"); loadFolders(); loadMessages("INBOX", 1); loadProfile(); loadScheduled(); loadSignatureFlag(); }); }} />
      )}
      {settingsOpen && (
        <SettingsModal api={api} accounts={accounts} activeEmail={activeEmail} canAddAccount={canAddAccount} onClose={() => setSettingsOpen(false)} onAddAccount={() => { setSettingsOpen(false); setAddAccountOpen(true); }} onRemoveAccount={(email) => removeAccount(email)} onOpenSignature={() => { setSettingsOpen(false); setSigModal(true); }} />
      )}
    </div>
  );
};
